<template>
	<h3 class="title mb_20 tal">{{ $t('c2c.buy.title') }}</h3>
	<div v-if="step === 0">
		<p class="mb_10">{{ $t('c2c.buy.head1') }}</p>
		<div class="info_box mb_20">
			<p class="info_text">
				{{ $t('c2c.buy.text1') }}<br />
				{{ $t('c2c.buy.text2') }}<br />
				{{ $t('c2c.buy.text3') }}<br />
				{{ $t('c2c.buy.text4') }}<br />
				{{ $t('c2c.buy.text5') }}
			</p>
		</div>
		<p class="mb_10">{{ $t('c2c.buy.head2') }}</p>
		<div class="info_box mb_20">
			<p class="info_text">
				{{ $t('c2c.buy.text6') }}<br />
				{{ $t('c2c.buy.text7') }}<br />
				{{ $t('c2c.buy.text8') }}
			</p>
		</div>
		<div class="button_area">
			<button class="" @click="$emit('close')">
				{{ $t('button.cancel') }}
			</button>
			<button class="fc_red" @click="stepNext">
				{{ $t('button.next') }}
			</button>
		</div>
	</div>

	<form @submit.prevent v-if="step === 1" class="buy_asset">
		<p class="mb_10">{{ $t('c2c.buy.head3') }}</p>
		<div class="asset_info d_flex justify_between">
			<div class="asset_box">
				<span :class="[sourceAsset.code, 'medium']"></span>
				<div>
					<p class="code mb_4">{{ sourceAsset.code }}</p>
					<p class="name">{{ sourceAsset.title }}</p>
				</div>
			</div>
			<div>
				<p class="fs_14">{{ $t('c2c.buy.head4') }}</p>
				<p>{{ dealInfo.source_amount }}</p>
			</div>
			<div>
				<p class="fs_14">{{ $t('c2c.buy.head5') }}</p>
				<p>{{ dealInfo.min_price }}</p>
			</div>
			<div>
				<p class="fs_14">{{ $t('c2c.buy.head6') }}</p>
				<p>{{ dealInfo.potential_source_amount }}</p>
			</div>
		</div>

		<div class="d_flex mb_20">
			<Extform
				class="border_t_black mt_20 w100 pt_20"
				v-for="extform in dealInfo.extforms"
				:key="extform.seq"
				v-bind="extform"
				v-model="extform.field_values"
				:readonly="true"
			/>
		</div>

		<div class="d_flex mb_20">
			<div v-if="sourceAccount" class="w50 mr_10">
				<p class="mb_10">{{ $t('c2c.buy.head7') }} {{ sourceAsset.code }}</p>
				<input
					v-if="sourceAccount"
					type="text"
					class="tar"
					:value="sourceAccount.balance"
					disabled
				/>
			</div>
			<div class="w50">
				<p class="mb_10">{{ $t('c2c.buy.head8') }}</p>
				<input type="text" class="tar" placeholder="0" v-model="payAmount" />
			</div>
		</div>

		<div class="d_flex align_items_start mb_20">
			<div class="w50 mr_10">
				<p class="mb_10">{{ $t('c2c.buy.head9') }}</p>
				<input
					type="text"
					class="mb_10 tar"
					:value="selectedMarket.fee_rate + ' %'"
					disabled
				/>
				<p>
					{{ $t('c2c.buy.text9') }}: {{ calcFeeAmount }}
					{{ $t('c2c.buy.text10') }}
				</p>
			</div>
			<div class="w50">
				<p class="mb_10">
					{{ $t('c2c.buy.head10') }} {{ targetAsset.code }}
					{{ $t('c2c.buy.head11') }}
				</p>
				<input type="text" class="tar" :value="formattingPayAmount" disabled />
			</div>
		</div>

		<div v-if="targetAccount">
			<p class="mb_10">{{ $t('c2c.buy.head12') }}</p>
			<div class="d_flex justify_between align_items_center selected_asset">
				<div class="asset_box">
					<span :class="[targetAsset.code, 'medium']"></span>
					<div>
						<p class="code mr_4">{{ targetAsset.code }}</p>
						<p class="name">{{ targetAsset.title }}</p>
					</div>
				</div>
				<p v-if="targetAccount" class="fs_15">{{ targetAccount.balance }}</p>
			</div>
		</div>
		<Extform
			class="border_t_black mt_20 pt_20"
			v-for="extform in extforms"
			:key="extform.seq"
			v-bind="extform"
			v-model="extform.field_values"
		/>

		<div class="button_area">
			<button class="" @click="stepBack">
				{{ $t('button.back') }}
			</button>
			<button
				class="fc_red normal_submit"
				:disabled="!canBuy"
				@click="buyHandler"
			>
				{{ $t('button.application') }}
			</button>
			<button class="fc_red disabled_submit" disabled>
				{{ $t('button.application') }}
			</button>
		</div>
	</form>
	<ModalMessage v-if="messageModal" @closeModal="messageModal = false">
		<div :class="'status_icon ' + modalIcon"></div>
		<p class="status_text">{{ modalStatusMessage }}</p>
		<div v-if="messageModalMode === 'information'" class="button_area">
			<button @click="messageModal = false">
				{{ $t('button.cancel') }}
			</button>
			<button class="fc_red" @click="createPrivateApplication">
				{{ $t('button.confirm') }}
			</button>
		</div>
		<div v-if="messageModalMode === 'success'" class="button_area">
			<button class="fc_red oneBtn" @click="toActivity">
				{{ $t('button.confirm') }}
			</button>
		</div>
	</ModalMessage>
</template>

<script>
import ModalMessage from '@/components/Layout/ModalMessage';
import {
	createPrivateApplication,
	fetchPrivateDeal,
	fetchPrivateMarkets,
	fetchPrivateMarketForms,
} from '@/api/market';
import { commonError } from '@/common/dialog';
import { mapGetters } from 'vuex';
import Extform from '@/components/Extform/Extform';

export default {
	name: 'C2cBuy',
	emits: ['close'],
	components: { ModalMessage, Extform },
	props: {
		dealSeq: {
			type: Number,
		},
	},
	data() {
		return {
			step: 0,
			markets: [],
			extforms: [],
			dealInfo: {},
			payAmount: null,
			messageModalMode: '',
			messageModal: false,
			modalIcon: '',
			modalStatusMessage: '',
		};
	},
	computed: {
		...mapGetters('assets', ['assets', 'registedAssets']),
		...mapGetters('account', ['accounts']),
		selectedMarket() {
			return this.markets.find(item => {
				return item.seq === this.dealInfo.market_seq;
			});
		},
		sourceAsset() {
			return this.registedAssets.find(item => {
				return item.seq === this.selectedMarket.source_asset_seq;
			});
		},
		targetAsset() {
			return this.registedAssets.find(item => {
				return item.seq === this.selectedMarket.target_asset_seq;
			});
		},
		sourceAccount() {
			return this.accounts.find(item => {
				return item.asset_seq === this.selectedMarket.source_asset_seq;
			});
		},
		targetAccount() {
			return this.accounts.find(item => {
				return item.asset_seq === this.selectedMarket.target_asset_seq;
			});
		},
		calcFeeAmount() {
			return this.formattingPayAmount * (this.selectedMarket.fee_rate / 100);
		},
		formattingPayAmount() {
			if (!this.payAmount) return null;
			const payAmount =
				parseFloat(this.payAmount) * parseFloat(this.dealInfo.min_price);
			return payAmount.toFixed(this.targetAsset.decimal_point);
		},
		canBuy() {
			return !!this.payAmount;
		},
	},
	watch: {
		step(value) {
			if (value === 1) this.payAmount = null;
		},
		payAmount(value) {
			if (!value) return;
			this.payAmount = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
		},
	},
	methods: {
		stepBack() {
			this.step--;
		},
		stepNext() {
			this.step++;
		},
		init() {
			Promise.all([this.loadPrivateMarkets(), this.loadDealInfo()]).then(() =>
				this.loadExtforms(),
			);
		},
		async loadPrivateMarkets() {
			const { data } = await fetchPrivateMarkets().catch(commonError);
			this.markets = data;
		},
		async loadDealInfo() {
			const { deal_info, field_values, forms } = await fetchPrivateDeal(
				this.dealSeq,
			).catch(commonError);
			const extforms = forms.map(form => ({
				...form,
				fields: [],
				field_values: [],
			}));
			field_values.forEach(item => {
				const form = extforms.find(form => form.seq === item.form_seq);
				form.fields.push(item);
				form.field_values.push(item);
			});
			deal_info.extforms = extforms;
			this.dealInfo = deal_info;
		},
		async loadExtforms() {
			const { forms } = await fetchPrivateMarketForms(
				this.dealInfo.market_seq,
				'MARKET_PRIVATE_APPLICATION',
			).catch(commonError);
			this.extforms = forms.map(item => ({ ...item, field_values: [] }));
		},
		buyHandler() {
			this.modalIcon = 'information';
			this.messageModalMode = 'information';
			this.modalStatusMessage = this.$t('modal.message.set_buy');
			this.messageModal = true;
		},
		async createPrivateApplication() {
			const params = {
				deal_seq: this.dealSeq,
				account_seq: this.sourceAccount?.seq,
				target_account_seq: this.targetAccount?.seq,
				source_amount: this.payAmount,
				price: this.dealInfo.min_price,
				field_values: [].concat(
					...this.extforms.map(item => item.field_values),
				),
			};
			await createPrivateApplication(params)
				.then(() => {
					this.$store.dispatch('account/getAccounts');
					this.modalIcon = 'success';
					this.messageModalMode = 'success';
					this.modalStatusMessage = this.$t('modal.message.registered');
				})
				.catch(e => {
					this.messageModal = false;
					commonError(e);
				});
		},
		toActivity() {
			this.$router.push({
				name: 'activity-c2c',
			});
		},
	},
	mounted() {
		this.init();
	},
};
</script>

<style scoped>
.disabled_submit {
	border-radius: 0 0 20px 0;
}
.buy_asset:invalid .normal_submit {
	display: none;
}
.buy_asset:valid .disabled_submit {
	display: none;
}
</style>
